import ListCard, { CardWrapper } from '@Components/ListCard/ListCard';
import AssetAssignModal from '@Components/Modals/AssetAssign/AssetAssignModal';
import AssetInputModal from '@Components/Modals/AssetInput/AssetInputModal';
import AssetUploadModal from '@Components/Modals/AssetUploadModal/AssetUploadModal.tsx';
import RadioList from '@Components/RadioList/RadioList';
import AccessoriesServices from '@Services/Accessories.services.ts';
import AssetServices from '@Services/Asset.services.ts';
import { Accessory, Asset, AssetStatus, AssetType, ExcelAsset, Location, MappedAsset } from '@Types/Asset.types';
import { PaginatedResponse } from '@Types/Response.types';
import { formatExcelData } from '@Utils/assets.utils';
import {
  ADMIN_ROLES,
  ASSET_CONTROL_PERMISSIONS,
  ASSET_FILTER_PARAMS,
  ASSET_MODAL_TYPE,
  ASSET_TAB_VALUES,
  EXCEL_FILE_LOCATION,
  PAGE_PROP,
  PAGE_SIZE,
  PAGINATED_DEFAULT,
  PATHS,
  PERSISTENT_FILTER_PROP,
  SEARCH_PROP,
  SEARCH_TIMER_INTERVAL,
  TAB_PROP,
  TAB_VALUES,
  TABLE_STRUCTURES,
} from '@Utils/constants';
import { downloadDocument } from '@Utils/document.utils.ts';
import { formatCreateValues } from '@Utils/form.utils';
import {
  formatAllAssetTag,
  formatAssetLocationRadioOptions,
  formatAssetStatusRadioOptions,
  formatAssetTypeRadioOptions,
} from '@Utils/format.utils.tsx';
import { clearInputs } from '@Utils/input.utils';
import { fillSearchParams } from '@Utils/navigation.utils';
import { handleRequestError } from '@Utils/request.utils';
import {
  Button,
  ErrorDisplay,
  Icons,
  Pagination,
  SearchBox,
  SectionCard,
  Toast,
  useTopHat,
  useUser,
} from '@kg-internal/pv3-uikit';
import cx from 'classnames';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import readXlsxFile from 'read-excel-file';
import AssetStatusServices from '~/Services/AssetStatus.services';
import AssetTypeServices from '~/Services/AssetType.services';
import LocationServices from '~/Services/Location.services';
import { createSingleSearchParameter } from '~/Utils/url.util';
import styles from './Assets.module.scss';

const Assets = () => {
  const user = useUser();
  const isAdmin = user.hasPermissions(ADMIN_ROLES);
  const isLead = user.hasPermissions(ASSET_CONTROL_PERMISSIONS.LEAD) && !isAdmin;

  const [assets, setAssets] = useState<PaginatedResponse<Asset>>(PAGINATED_DEFAULT);
  const [accessories, setAccessories] = useState<PaginatedResponse<Accessory>>(PAGINATED_DEFAULT);
  const [searchParams, setSearchParams] = useSearchParams();
  const [offices, setOffices] = useState<Location[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [assetStatuses, setAssetStatuses] = useState<AssetStatus[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<string | undefined>(
    searchParams.has(ASSET_FILTER_PARAMS.OFFICE_ID)
      ? decodeURI(searchParams.get(ASSET_FILTER_PARAMS.OFFICE_ID)!)
      : undefined,
  );
  const [selectedAssetType, setSelectedAssetType] = useState<string | undefined>(
    searchParams.has(ASSET_FILTER_PARAMS.ASSET_TYPE)
      ? decodeURI(searchParams.get(ASSET_FILTER_PARAMS.ASSET_TYPE)!)
      : undefined,
  );
  const [selectedAssetStatus, setSelectedAssetStatus] = useState<string | undefined>(
    searchParams.has(ASSET_FILTER_PARAMS.ASSET_STATUS)
      ? decodeURI(searchParams.get(ASSET_FILTER_PARAMS.ASSET_STATUS)!)
      : undefined,
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const excelRef = useRef<HTMLInputElement>(null);
  const [excelData, setExcelData] = useState<Partial<ExcelAsset>[]>([]);
  const [showAssetUpload, setShowAssetUpload] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(
    searchParams.has(PAGE_PROP) ? parseInt(searchParams.get(PAGE_PROP) || '', 10) : 1,
  );

  const [currentTab, setCurrentTab] = useState<number>(
    !isLead && searchParams.has(TAB_PROP) ? parseInt(searchParams.get(TAB_PROP) || '', 10) : TAB_VALUES.ASSETS,
  );

  const [search, setSearch] = useState<string>(searchParams.get(SEARCH_PROP) || '');

  const [title, setTitle] = useState<string>(currentTab === TAB_VALUES.ASSETS ? 'Assets' : 'Accessories');

  const [showNewAssetModal, setShowNewAssetModal] = useState<boolean>(false);
  const [showAssignAssetModal, setShowAssignAssetModal] = useState<boolean>(false);

  const block = 'assets';

  const mapAssetSearchParams = ({
    tab = currentTab,
    page = currentPage,
    assetType = selectedAssetType,
    assetStatus = selectedAssetStatus,
    office = selectedOffice,
    searchFilter = search,
  }) => {
    return {
      [PAGE_PROP]: page,
      [TAB_PROP]: tab,
      [ASSET_FILTER_PARAMS.ASSET_TYPE]: assetType,
      [ASSET_FILTER_PARAMS.ASSET_STATUS]: assetStatus,
      [ASSET_FILTER_PARAMS.OFFICE_ID]: office,
      [SEARCH_PROP]: searchFilter,
    };
  };

  const moreDetailUrlSearchParam = useMemo(() => {
    return createSingleSearchParameter(PERSISTENT_FILTER_PROP, mapAssetSearchParams({}));
  }, [searchParams]);

  const { setTopHat } = useTopHat({
    activeNav: 'nav-assets',
    activeTab: currentTab === TAB_VALUES.ASSETS ? 'tab-assets' : 'tab-accessories',
    tabs: isLead ? [] : ASSET_TAB_VALUES,
    breadcrumbs: [
      {
        title: 'Assets',
        // when on asset page, make clicking the tophat not change URL
        href: `.`,
        id: 'Assets',
      },
    ],
  });
  let timer: number;

  const getAssets = (page?: number) => {
    if (!isAdmin) {
      AssetServices.getAssets(page || currentPage, true)
        .then((response) => {
          setAssets(response.data);
          setErrorMessage(undefined);
        })
        .catch((error) => {
          handleRequestError(error, setErrorMessage);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      searchAssets('', page);
    }
  };

  const searchAssets = (toSearch: string, page?: number) => {
    const assetFilter = {
      assetStatus: selectedAssetStatus ? [selectedAssetStatus] : undefined,
      assetType: selectedAssetType ? [selectedAssetType] : undefined,
      officeName: selectedOffice ? [selectedOffice] : undefined,
    };
    AssetServices.getAssetsSearch({ search: toSearch, filter: assetFilter }, page || currentPage)
      .then((response) => {
        setAssets(response.data);
        setErrorMessage(undefined);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getAccessories = (page?: number) => {
    AccessoriesServices.getAccessories(page || currentPage, true)
      .then((response) => {
        setAccessories(response.data);
        setErrorMessage(undefined);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getOffices = async () => {
    try {
      const officesData = await LocationServices.getLocations(null, false);
      setOffices(officesData.data.docs);
    } catch (_error) {
      setErrorMessage('Error loading offices');
    }
  };

  const getAssetTypes = async () => {
    try {
      const assetTypeData = await AssetTypeServices.getAssetTypes(null, false);
      setAssetTypes(assetTypeData.data.docs);
    } catch (_error) {
      setErrorMessage('Error Loading Asset Types');
    }
  };

  const getAssetStatuses = async () => {
    try {
      const assetStatusData = await AssetStatusServices.getAssetStatus(null, false);
      setAssetStatuses(assetStatusData.data.docs);
    } catch (_error) {
      setErrorMessage('Error Loading Asset Statuses');
    }
  };

  const searchAccessory = (toSearch: string, page?: number) => {
    AccessoriesServices.searchAccessories({ search: toSearch }, page || currentPage)
      .then((response) => {
        setAccessories(response.data);
        setErrorMessage(undefined);
      })
      .catch((error) => {
        handleRequestError(error, setErrorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSearch = (value: string) => {
    // Handle search
    setLoading(true);
    // Wait until user is done typing to make search
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      if (currentTab === TAB_VALUES.ASSETS) {
        searchAssets(value, 1);
      }
      if (currentTab === TAB_VALUES.ACCESSORIES) {
        searchAccessory(value, 1);
      }
    }, SEARCH_TIMER_INTERVAL);

    const mappedFilters = mapAssetSearchParams({ searchFilter: value, page: 1 });
    fillSearchParams(setSearchParams, mappedFilters);
    setSearch(value);
    setCurrentPage(1);
  };

  const renderTable = (selectedTab: number) => {
    if (selectedTab === TAB_VALUES.ASSETS) {
      return (
        <ListCard<MappedAsset>
          data={formatAllAssetTag(assets.docs)}
          withDetails
          detailsPath={PATHS.ASSETS}
          detailsSearchParams={moreDetailUrlSearchParam}
          loading={loading}
          error={errorMessage}
          title={title}
          structure={TABLE_STRUCTURES.ASSET}
          searchEntered={!!search}
        />
      );
    }
    if (selectedTab === TAB_VALUES.ACCESSORIES) {
      return (
        <ListCard<Accessory>
          data={accessories.docs}
          withDetails={isAdmin}
          detailsPath={PATHS.ACCESSORIES}
          detailsSearchParams={moreDetailUrlSearchParam}
          loading={loading}
          error={errorMessage}
          title={title}
          structure={TABLE_STRUCTURES.ACCESSORY}
          searchEntered={!!search}
        />
      );
    }
    return (
      <CardWrapper title={title}>
        <ErrorDisplay inline details='Invalid tab value found' />
      </CardWrapper>
    );
  };

  const onCreationSuccess = () => {
    Toast.success('Asset successfully created');
    setLoading(true);
    setCurrentTab(TAB_VALUES.ASSETS);
    setTopHat({ activeTab: 'tab-assets' });
    setTitle('Assets');
    setCurrentPage(1);
    getAssets(1);
  };

  const handleNewAssetModalClose = () => {
    setShowNewAssetModal(false);
  };

  const createSubmitHandler = (assetFormData: FormData) => AssetServices.createAsset(assetFormData);

  const uploadAssets = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      readXlsxFile(file)
        .then((rows) => {
          const data = formatExcelData(rows as [][]);
          if (data.length === 0) {
            Toast.warning('No data found in the excel file!');
          } else {
            setExcelData(data);
            setShowAssetUpload(true);
          }
        })
        .catch(() => {
          Toast.warning('Could not read the uploaded file. Please ensure you are using the template format.');
        });
    } else {
      Toast.warning('Error');
    }
  };

  const closeUploadModal = () => {
    setShowAssetUpload(false);
    setExcelData([]);
  };

  const clearFilters = () => {
    setCurrentPage(1);
    setSelectedOffice(undefined);
    setSelectedAssetType(undefined);
    setSelectedAssetStatus(undefined);
  };

  useEffect(() => {
    const fetchFilterData = async () => {
      setLoading(true);
      await Promise.all([getOffices(), getAssetTypes(), getAssetStatuses()]);
      setLoading(false);
    };
    if (isAdmin) {
      fetchFilterData();
    }
  }, []);

  useEffect(() => {
    // If tab changes, clear everything
    if (searchParams.get(TAB_PROP) !== currentTab.toString()) {
      const tab = parseInt(searchParams.get(TAB_PROP) || '0', 10);
      setCurrentTab(tab);
      // Handle values on tab change
      if (tab === TAB_VALUES.ASSETS) {
        setTopHat({ activeTab: 'tab-assets' });
        setTitle('Assets');
      }
      if (tab === TAB_VALUES.ACCESSORIES) {
        setTopHat({ activeTab: 'tab-accessories' });
        setTitle('Accessories');
      }
      setSearch('');
      clearInputs('search');
      setSelectedAssetStatus(undefined);
      setSelectedAssetType(undefined);
      setSelectedOffice(undefined);
      setCurrentPage(1);

      // After switching tabs, page will try to search for assets
      // Stop search request when we switch tabs
      if (timer) {
        setLoading(false);
        clearTimeout(timer);
      }
      return;
    }

    // When URL changes, we should change filters
    if (searchParams.get(PAGE_PROP) !== currentTab.toString()) {
      setCurrentPage(parseInt(searchParams.get(PAGE_PROP) || '1', 10));
    }
    if (decodeURI(searchParams.get(ASSET_FILTER_PARAMS.ASSET_STATUS)!) !== selectedAssetStatus) {
      if (searchParams.get(ASSET_FILTER_PARAMS.ASSET_STATUS) === null) {
        setSelectedAssetStatus(undefined);
      } else {
        setSelectedAssetStatus(decodeURI(searchParams.get(ASSET_FILTER_PARAMS.ASSET_STATUS)!));
      }
    }
    if (decodeURI(searchParams.get(ASSET_FILTER_PARAMS.ASSET_TYPE)!) !== selectedAssetType) {
      if (searchParams.get(ASSET_FILTER_PARAMS.ASSET_TYPE) === null) {
        setSelectedAssetType(undefined);
      } else {
        setSelectedAssetType(decodeURI(searchParams.get(ASSET_FILTER_PARAMS.ASSET_TYPE)!));
      }
    }
    if (decodeURI(searchParams.get(ASSET_FILTER_PARAMS.OFFICE_ID)!) !== selectedOffice) {
      if (searchParams.get(ASSET_FILTER_PARAMS.OFFICE_ID) === null) {
        setSelectedOffice(undefined);
      } else {
        setSelectedOffice(decodeURI(searchParams.get(ASSET_FILTER_PARAMS.OFFICE_ID)!));
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Handle page changes
    if (!loading) {
      setLoading(true);
      if (currentTab === TAB_VALUES.ASSETS) {
        if (search && isAdmin) {
          searchAssets(search);
        } else {
          getAssets();
        }
      }
      if (currentTab === TAB_VALUES.ACCESSORIES) {
        if (search) {
          searchAccessory(search);
        } else {
          getAccessories();
        }
      }
    }
    const searchParams = mapAssetSearchParams({});
    fillSearchParams(setSearchParams, searchParams);
  }, [currentPage, selectedOffice, selectedAssetStatus, selectedAssetType, currentTab]);

  const handleAssignAssetModalClose = () => {
    setShowAssignAssetModal(false);
  };

  const onAssignSuccess = () => {
    getAssets(1);
  };

  return (
    <div className='grid'>
      {isAdmin && (
        <div className='col-12 col-md-3'>
          <SectionCard>
            <>
              <SearchBox
                id='search-asset'
                placeholder='Search for...'
                disabled={false}
                showLabel={false}
                focusOnRender
                fullWidth
                value={search}
                onChange={handleSearch}
                onSubmit={(value) => handleSearch(value as string)}
                defaultValue={search}
                aria-label='Asset Searchbox'
              />
              <Button
                type='primary'
                className='button-with-icon'
                fullWidth
                onClick={() => setShowNewAssetModal(true)}
                aria-label='New Asset Button'>
                {Icons.Plus} Add new Asset
              </Button>
              <br />
              <Button
                type='secondary'
                fullWidth
                className={`button-with-icon ${styles[`${block}__button`]}`}
                onClick={() => setShowAssignAssetModal(true)}
                aria-label='Assign Asset Button'>
                <i className='fa-solid fa-user-plus' /> Assign to User
              </Button>
              <br />
              <div className='divider' />
              <Button
                type='primary'
                className='button-with-icon'
                fullWidth
                onClick={() => {
                  if (excelRef.current) {
                    excelRef.current.value = '';
                    excelRef.current.click();
                  }
                }}
                aria-label='Upload Asset Excel Button'>
                <i className='fa-regular fa-cloud-arrow-up' /> Upload Assets with Excel
                <input type='file' ref={excelRef} style={{ display: 'none' }} onChange={uploadAssets} />
              </Button>
              <br />
              <Button
                type='secondary'
                className='button-with-icon'
                fullWidth
                onClick={() => downloadDocument(EXCEL_FILE_LOCATION)}
                aria-label='Download Asset Excel Button'>
                {Icons.Download} Download Excel Template
              </Button>
              {currentTab === TAB_VALUES.ASSETS && (
                <>
                  <div className='divider' />
                  <div className={`${styles[`${block}__filters`]}`}>
                    <h2 className={`${styles[`${block}__filters-title`]}`}>{'Filters'}</h2>
                    {(selectedOffice || selectedAssetType || selectedAssetStatus) && (
                      <button
                        type='button'
                        className={`text-with-icon ${styles[`${block}__filters-button`]}`}
                        onClick={() => clearFilters()}>
                        <i className='fa-solid fa-trash' />
                        Clear
                      </button>
                    )}
                  </div>
                  <RadioList
                    radioOptions={formatAssetTypeRadioOptions(assetTypes)}
                    title={'Asset Type'}
                    handleOnClick={(value) => {
                      setCurrentPage(1);
                      setSelectedAssetType(value as string);
                    }}
                    radioName='Asset Type'
                    currentValue={selectedAssetType}
                  />
                  <RadioList
                    radioOptions={formatAssetStatusRadioOptions(assetStatuses)}
                    title={'Asset Status'}
                    handleOnClick={(value) => {
                      setCurrentPage(1);
                      setSelectedAssetStatus(value as string);
                    }}
                    radioName='Asset Status'
                    currentValue={selectedAssetStatus}
                  />
                  <RadioList
                    radioOptions={formatAssetLocationRadioOptions(offices)}
                    title={'Office'}
                    handleOnClick={(value) => {
                      setCurrentPage(1);
                      setSelectedOffice(value as string);
                    }}
                    radioName='Office'
                    currentValue={selectedOffice}
                  />
                </>
              )}
            </>
          </SectionCard>
        </div>
      )}
      <div
        className={cx('col-12', {
          'col-md-9': isAdmin,
        })}>
        {renderTable(currentTab)}
      </div>
      <div className={cx('col-12', 'content-middle', { 'content-right-md': isAdmin })}>
        <Pagination
          count={currentTab === TAB_VALUES.ASSETS ? assets.totalDocs : accessories.totalDocs}
          perPage={PAGE_SIZE}
          currentPage={currentPage - 1}
          onChange={(value) => setCurrentPage(value + 1)}
        />
      </div>
      {isAdmin && (
        <>
          <AssetInputModal
            show={showNewAssetModal}
            onClose={handleNewAssetModalClose}
            onSuccess={onCreationSuccess}
            onSubmit={createSubmitHandler}
            formatter={formatCreateValues}
            title='Create Asset'
            type={ASSET_MODAL_TYPE.ASSET}
          />
          <AssetUploadModal
            data={excelData}
            isOpen={showAssetUpload}
            onClose={closeUploadModal}
            onCreate={onCreationSuccess}
          />

          <AssetAssignModal
            showModal={showAssignAssetModal}
            onClose={handleAssignAssetModalClose}
            onSuccess={onAssignSuccess}
          />
        </>
      )}
    </div>
  );
};
export default Assets;
